/**
 * Thin API client. Sends credentials (cookies) automatically and attaches the
 * double-submit CSRF token to every state-changing request. All crypto happens
 * elsewhere; this layer only moves JSON (ciphertext + metadata) over the wire.
 */

let csrfToken: string | null = null;
export function setCsrfToken(token: string | null) {
  csrfToken = token;
}
export function getCsrfToken() {
  return csrfToken;
}

const BASE = import.meta.env.VITE_API_BASE ?? '';

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiResult<T = unknown> {
  status: number;
  body: T;
}

export async function apiFetch<T = unknown>(path: string, init: { method?: string; body?: unknown; query?: Record<string, string> } = {}): Promise<ApiResult<T>> {
  let url = `${BASE}${path}`;
  if (init.query) {
    url += (path.includes('?') ? '&' : '?') + new URLSearchParams(init.query).toString();
  }
  const method = init.method ?? 'GET';
  const headers: Record<string, string> = {};
  if (init.body !== undefined) headers['Content-Type'] = 'application/json';
  if (csrfToken && method !== 'GET') headers['X-CSRF-Token'] = csrfToken;

  const res = await fetch(url, {
    method,
    headers,
    credentials: 'include',
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const text = await res.text();
  let body: unknown = text;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    /* keep raw text */
  }
  if (!res.ok) {
    const msg = (body as { error?: string })?.error ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, msg, (body as { code?: string })?.code);
  }
  return { status: res.status, body: body as T };
}

export const api = {
  get: <T = unknown>(path: string, query?: Record<string, string>) => apiFetch<T>(path, { method: 'GET', query }),
  post: <T = unknown>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  put: <T = unknown>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PUT', body }),
  del: <T = unknown>(path: string, query?: Record<string, string>) => apiFetch<T>(path, { method: 'DELETE', query }),
};
